// Shared building blocks for every config kind.
import { z } from "zod";

/** U+2013 en dash, U+2014 em dash. House rule: never in text a prospect can see or hear. */
export const DASH_PATTERN = /[\u2013\u2014]/;

/**
 * Behavior text. No em or en dashes anywhere in behavior config: Otto copies
 * the style of what he is given, and the design's publish check refuses a
 * dash anywhere (design 6.2).
 */
export function behaviorText(max: number) {
  return z
    .string()
    .min(1)
    .max(max)
    .refine((s) => !DASH_PATTERN.test(s), { message: "No em or en dashes (house rule). Use a comma, colon or period." });
}

/** Stable ids: lowercase letters, digits and underscores, starting with a letter. */
export const Id = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/, "Use lowercase letters, digits and underscores, starting with a letter");

/**
 * Every seeded or edited document may carry a visible label such as
 * "v0 DRAFT, to be redefined with Wes". The admin shows it on every screen
 * that displays the document.
 */
export const DraftLabel = z.string().max(200).optional();

/** The versioned kinds stored in config_versions. Switches and caps are settings, not versions. */
export const ConfigKind = z.enum(["brief", "conduct", "info_map", "agenda", "facts", "timing", "voice", "coach"]);
export type ConfigKind = z.infer<typeof ConfigKind>;
export const CONFIG_KINDS: readonly ConfigKind[] = ConfigKind.options;

export const ConfigStatus = z.enum(["draft", "active", "retired"]);
export type ConfigStatus = z.infer<typeof ConfigStatus>;

export const VersionNumber = z.number().int().min(1);

/** One row of config_versions, without its body. */
export const ConfigVersionMeta = z.object({
  kind: ConfigKind,
  version: VersionNumber,
  status: ConfigStatus,
  created_at: z.iso.datetime(),
  created_by: z.string().min(1).max(200),
  note: z.string().max(1000).nullable(),
  /** The version this one was edited from, for the history view. */
  based_on_version: VersionNumber.nullable().default(null),
  /** When and by whom it was last made active; null for a version never activated. */
  activated_at: z.iso.datetime().nullable().default(null),
  activated_by: z.string().min(1).max(200).nullable().default(null),
});
export type ConfigVersionMeta = z.infer<typeof ConfigVersionMeta>;

export const ConfigMode = z.enum(["video", "voice"]);
export type ConfigMode = z.infer<typeof ConfigMode>;

/** Adds an issue for every value that appears more than once. */
export function requireUnique(values: readonly string[], ctx: z.RefinementCtx, path: (string | number)[], what: string) {
  const seen = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) ctx.addIssue({ code: "custom", path, message: `Duplicate ${what}: ${value}` });
    seen.add(value);
  }
}

// ---------------------------------------------------------------------------
// Templates: behavior text with {placeholders} filled by code at run time.
// ---------------------------------------------------------------------------

/** Every placeholder any template may use. Unknown placeholders fail validation. */
export const TEMPLATE_PLACEHOLDERS = ["first_name", "business_name", "gap_seconds", "minutes_left"] as const;
export type TemplatePlaceholder = (typeof TEMPLATE_PLACEHOLDERS)[number];

const PLACEHOLDER_PATTERN = /\{([a-z_]+)\}/g;

export function templateText(max: number) {
  return behaviorText(max).refine(
    (s) => [...s.matchAll(PLACEHOLDER_PATTERN)].every((m) => (TEMPLATE_PLACEHOLDERS as readonly string[]).includes(m[1] ?? "")),
    { message: `Unknown placeholder. Allowed: ${TEMPLATE_PLACEHOLDERS.map((p) => `{${p}}`).join(", ")}` },
  );
}

/**
 * Fill a template. Throws on a placeholder with no value: a note with a raw
 * "{first_name}" in it must never reach Otto (loud, not silent).
 */
export function renderTemplate(template: string, values: Partial<Record<TemplatePlaceholder, string | number>>): string {
  return template.replace(PLACEHOLDER_PATTERN, (whole, name: string) => {
    const value = values[name as TemplatePlaceholder];
    if (value === undefined) throw new Error(`renderTemplate: no value for ${whole}`);
    return String(value);
  });
}
